import React, { createContext, useContext, useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppAuth } from './auth/appAuth';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MainLayout from './layouts/MainLayout';
import SimpleStaticLayout from './layouts/SimpleStaticLayout';
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import Logout from './components/Logout';
import ClearAuthPage from './pages/ClearAuthPage';
import BrandTracker from './components/BrandTracker';
import PillarBreakdown from './pages/PillarBreakdown';
import KeywordAnalysis from './pages/KeywordAnalysis';
import AdminDashboard from './pages/AdminDashboard';
import AdminOrganizations from './pages/AdminOrganizations';
import AdminUsers from './pages/AdminUsers';
import AnalyticsTracker from './components/AnalyticsTracker';
import AdminDashboardLogs from './pages/AdminDashboardLogs';
import AdminChatbotLogs from './pages/AdminChatbotLogs';
import AdminSocialSummaries from './pages/AdminSocialSummaries';
import AdminApiStatus from './pages/AdminApiStatus';
import OrganizationDetailsPage from './pages/OrganizationDetailsPage';
import NoAccessPage from './pages/NoAccessPage';
import { useEffect } from 'react';
import { useAxiosClient } from './utils/useAxiosClient';
import WatchdogDashboard from './components/WatchdogDashboard';
import ImageComparison from './pages/ImageComparison';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './theme';
import FilterContextProvider from './context/FilterContext';
import OrgRedirector from './components/OrgRedirector';
import EmployeeBrandGeneralMetrics from './pages/EmployeeBrandGeneralMetrics';
import EmployeeBrandPersonalMetrics from './pages/EmployeeBrandPersonalMetrics';
import { useNavigate } from 'react-router-dom';
import ChatTest from './pages/ChatTest';
import GeminiImageLab from './pages/GeminiImageLab';

// Development auth bypass - set REACT_APP_BYPASS_AUTH=true in .env.development
const DEV_BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

// Create theme context
interface ThemeContextType {
  mode: 'light' | 'dark';
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  mode: 'light',
  toggleTheme: () => {},
});

export const useThemeContext = () => useContext(ThemeContext);

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { isAuthenticated, isLoading } = useAppAuth();
  const location = useLocation();

  // Bypass auth in development mode
  if (DEV_BYPASS_AUTH) {
    return element;
  }

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  return isAuthenticated
    ? element
    : (
        <Navigate
          to="/login"
          replace
          state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
        />
      );
};

const DefaultOrgRedirect: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const axiosClient = useAxiosClient();
  const { isAuthenticated, loginWithRedirect } = useAppAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchDefaultOrg = async () => {
      // Bypass auth check in dev mode
      if (!DEV_BYPASS_AUTH) {
        // If not authenticated, redirect to login
        if (!isAuthenticated) {
          await loginWithRedirect();
          return;
        }
      }

      // If authenticated (or dev bypass), fetch the default organization
      try {
        const response = await axiosClient.get('/api/default-org');
        
        const orgId = response.data.org_id;
        // If no default org is found, redirect to a fallback org
        if (!orgId) {
          navigate('/org/158', { replace: true });
          return;
        }

        // Redirect using navigate instead of window.location
        navigate(`/org/${orgId}`, { replace: true });
      } catch (error) {
        console.error('❌ Error fetching default org:', error);
        navigate('/org/158', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefaultOrg();
  }, [axiosClient, isAuthenticated, loginWithRedirect, navigate]);

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return null;
};

function App() {
  const { isLoading } = useAppAuth();
  // Initialize theme mode from localStorage or default to 'light'
  const [mode, setMode] = useState<'light' | 'dark'>(
    localStorage.getItem('themeMode') === 'dark' ? 'dark' : 'light'
  );

  // Create theme toggle function
  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  // Create theme context value with memoization to prevent unnecessary rerenders
  const themeContextValue = useMemo(() => ({
    mode,
    toggleTheme
  }), [mode, toggleTheme]);

  // Create MUI theme based on current mode
  const theme = useMemo(() => createAppTheme(mode), [mode]);

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          {/* Renders nothing; records pageviews and clicks. Must be inside
              Router because it reads the current location. */}
          <AnalyticsTracker />
          <Routes>
            {/* Public, Watchdog, Admin, Root Redirect routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/clear" element={<ClearAuthPage />} />
            <Route path="/no-access" element={<NoAccessPage />} />
            <Route path="/callback" element={<CallbackPage />} />
            <Route path="/watchdog" element={<WatchdogDashboard />} />
            <Route path="/chat-test" element={<ChatTest />} />
            <Route path="/gemini-lab" element={<GeminiImageLab />} />
            
            {/* Admin routes */}
            <Route 
              path="/admin" 
              element={<ProtectedRoute element={<AdminDashboard />} />} 
            />
            <Route 
              path="/admin/organizations" 
              element={<ProtectedRoute element={<AdminOrganizations />} />} 
            />
            <Route
              path="/admin/org/:orgId"
              element={<ProtectedRoute element={<OrganizationDetailsPage />} />}
            />
            <Route 
              path="/admin/users" 
              element={<ProtectedRoute element={<AdminUsers />} />} 
            />
            <Route 
              path="/admin/dashboard-logs" 
              element={<ProtectedRoute element={<AdminDashboardLogs />} />} 
            />
            <Route 
              path="/admin/chatbot-logs" 
              element={<ProtectedRoute element={<AdminChatbotLogs />} />} 
            />
            <Route 
              path="/admin/social-summaries" 
              element={<ProtectedRoute element={<AdminSocialSummaries />} />} 
            />
            <Route
              path="/admin/api-status"
              element={<ProtectedRoute element={<AdminApiStatus />} />}
            />
            <Route path="/" element={<DefaultOrgRedirect />} />
            <Route 
              path="/org/:orgId" 
              element={<ProtectedRoute element={<OrgRedirector />} />} 
            />

            {/* Standard dashboard routes - explicitly use MainLayout + FilterContext */}
            <Route 
              path="/org/:orgId/brand-tracker" 
              element={
                <ProtectedRoute 
                  element={
                    <FilterContextProvider>
                      <MainLayout />
                    </FilterContextProvider>
                  }
                />
              } 
            >
              <Route index element={<BrandTracker />} />
            </Route>

            <Route 
              path="/org/:orgId/pillar-breakdown" 
              element={
                <ProtectedRoute 
                  element={
                    <FilterContextProvider>
                      <MainLayout />
                    </FilterContextProvider>
                  }
                />
              } 
            >
              <Route index element={<PillarBreakdown />} />
            </Route>

            <Route 
              path="/org/:orgId/keyword-analysis" 
              element={
                <ProtectedRoute 
                  element={
                    <FilterContextProvider>
                      <MainLayout />
                    </FilterContextProvider>
                  }
                />
              } 
            >
              <Route index element={<KeywordAnalysis />} />
            </Route>

            <Route 
              path="/org/:orgId/image-comparison" 
              element={
                <ProtectedRoute 
                  element={
                    <FilterContextProvider>
                      <MainLayout />
                    </FilterContextProvider>
                  }
                />
              } 
            >
              <Route index element={<ImageComparison />} />
            </Route>

            {/* New Employee Brand Dashboard route with MainLayout and nested routes */}
            <Route 
              path="/org/:orgId/employee-brand" 
              element={
                <ProtectedRoute 
                  element={
                    <FilterContextProvider>
                      <MainLayout />
                    </FilterContextProvider>
                  }
                />
              } 
            >
              {/* Default route to General Metrics */}
              <Route index element={<Navigate to="general" replace />} /> 
              <Route path="general" element={<EmployeeBrandGeneralMetrics />} />
              <Route path="personal" element={<EmployeeBrandPersonalMetrics />} />
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
